import { useCallback, useEffect, useMemo, useState } from 'react';
import { CellProps, Column } from 'react-table';

import useTableInstance from '../../../hooks/useTableInstance';
import QuantityCell from '../components/QuantityCell';
import { GoodsData } from '../../../models/Goods';
import { StockDTO } from '../../../api/api';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrderGoodsList, setOrderGoodsList } from '../../../stateManagement/reducers/orderGoodsReducer';

export const useOrderSelectedGoodsList = (stockErrorCallback: (hasError: boolean) => void, stock: StockDTO[]) => {
  const dispatch = useDispatch();

  const selectedGoods = useSelector(selectOrderGoodsList);

  const setGoodsAmount = useCallback(
    (value: number, goodsId?: string) => {
      if (!goodsId) return;

      const tmpSelectedGoods = selectedGoods.map((selectedGood) => {
        return selectedGood.itemId === goodsId ? { ...selectedGood, qty: value } : selectedGood;
      });
      dispatch(setOrderGoodsList(tmpSelectedGoods));
    },
    [dispatch, selectedGoods]
  );

  const removeGoods = useCallback(
    (goodsId?: string) => {
      if (!goodsId) return;
      const tmpSelectedGoods = selectedGoods.filter((selectedGood) => {
        return selectedGood.itemId !== goodsId;
      });
      dispatch(setOrderGoodsList(tmpSelectedGoods));
    },
    [dispatch, selectedGoods]
  );

  const data = useMemo((): GoodsData[] => {
    if (!selectedGoods.length || !stock) return [];

    return selectedGoods.map((item) => {
      const idSuffix = item.batchNumber || item.serialNumber;
      return {
        goodsId: item.itemId,
        goodsIdDisplayName: item.itemId + (idSuffix ? ` (${idSuffix})` : ''),
        goodsName: item.name ?? '-',
        amount: item.qty ?? '0',
        available: stock.find((good) => good.product?.itemId === item.itemId)?.available?.toString() ?? '0'
      } as GoodsData;
    });
  }, [selectedGoods, stock]);

  useEffect(() => {
    stockErrorCallback(data.some((good) => (good.amount ?? 0) > parseInt(good.available ?? '0')));
  }, [data, stockErrorCallback]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Varenummer',
        accessor: 'goodsIdDisplayName',
        disableFilters: true,
        width: 100
      },
      {
        Header: 'Varenavn',
        accessor: 'goodsName',
        disableFilters: true,
        width: 300
      },
      {
        Header: 'På lager',
        accessor: 'available',
        disableFilters: true
      },
      {
        Header: '',
        accessor: 'amount',
        disableFilters: true,
        disableSortBy: true,
        Cell: (cellProps: CellProps<GoodsData>) => {
          return (
            <QuantityCell
              minValue={1}
              row={cellProps.row.original}
              qty={cellProps.value}
              removeGoods={removeGoods}
              setGoodsAmount={setGoodsAmount}
            />
          );
        }
      }
    ] as Column<GoodsData>[];
  }, [removeGoods, setGoodsAmount]);

  const tableInstance = useTableInstance<GoodsData>(data, columns, {
    initialState: {
      pageSize: 6000,
      pageIndex: 0
    }
  });

  return { tableInstance };
};
